import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import BZAPIService from '../../services/BZAPIService';
import { BUG_CVE_REVIEW_STATES, AUTO_CONFIG_REMOVED, NOT_REVIEWED } from '../plan/inventory/reviewStates';
import { estimatePages, PAGE_BUDGET } from './exportCost';

const TYPES = [
  {
    value: 'summary',
    label: 'Summary',
    hint: 'Only bugs and CVEs someone reviewed, with risk tiles.'
  },
  {
    value: 'full',
    label: 'Key releases',
    hint: 'Every bug and CVE. Release columns show your versions plus a count of the rest.'
  },
  {
    value: 'full_all_releases',
    label: 'Full',
    hint: 'Every bug and CVE, with the complete affected-release history for each.'
  }
];

const STEP_LABELS = ['Report type', 'Detail level', 'Versions', 'Confirm'];

// "Config removed (auto)" is computed, never stored, and must never be offered as a
// choice. It is not in BUG_CVE_REVIEW_STATES today; the filter is a guard against it
// being added there later, which is exactly the drift reviewStates.js exists to prevent.
const SELECTABLE_STATES = BUG_CVE_REVIEW_STATES.filter(
  s => s.value !== AUTO_CONFIG_REMOVED && !/\(auto\)/i.test(s.value)
);

// Shared DOM id for whichever "why is Continue disabled" message is currently
// visible, so the disabled button can point screen readers at it.
const BLOCK_MESSAGE_ID = 'export-wizard-block-reason';

/**
 * Fraction of unique items the report will render as full detail blocks.
 *
 * Advisory mirror of `_expanded_share` in
 * service-rest-api/plan/pdfExport/start_export_job.py. The server derives the real
 * ratio from per-state row counts it queries itself; the browser has no such
 * breakdown, so the non-empty case is a deliberate approximation.
 *
 * The two saturating cases are NOT approximations and must not drift:
 *   - an EMPTY selection means "expand everything" to the renderer (an omitted
 *     field and an explicit [] are indistinguishable on the wire), so it is 1.0,
 *     not 0. Reporting a near-zero estimate for the largest possible report is
 *     the fail-open this whole gate exists to close.
 *   - "Not reviewed" covers every item with no review row, so it is also 1.0.
 */
export const expandedShareFor = (exportMode, detailStates) => {
  if (exportMode === 'summary') return 0.02;
  const states = detailStates || [];
  if (states.length === 0) return 1.0;
  if (states.includes(NOT_REVIEWED)) return 1.0;
  return 0.02 + 0.01 * Math.max(states.length - 1, 0);
};

/**
 * Item count for one version, in the shape estimatePages prices.
 *
 * Returns `null` — not 0 — when the version carries none of `items`,
 * `bugCount`, or `cveCount`. That shape is exactly what a `PlanProjectVersion`
 * with counts not yet joined in looks like: only `versionId`/`state`. Treating
 * "no count fields at all" as 0 would price the version at 0 pages, disable
 * the cap entirely, and let the estimate confidently understate the report —
 * the server's own principle is that unknown scope is rejected, not assumed
 * empty (see `_select_project_versions` in
 * service-rest-api/plan/pdfExport/start_export_job.py), so the client mirrors
 * that: unknown stays unknown and callers must check for `null` before using
 * the result. A version that genuinely has zero items (an explicit `items: 0`,
 * or explicit `bugCount`/`cveCount` fields that sum to zero) still prices at 0.
 */
const itemsFor = version => {
  if (version == null) return null;
  const { items, bugCount, cveCount } = version;
  if (items !== undefined && items !== null) {
    const explicit = Number(items);
    return Number.isFinite(explicit) ? Math.max(explicit, 0) : null;
  }
  if (bugCount === undefined && cveCount === undefined) return null;
  return (Number(bugCount) || 0) + (Number(cveCount) || 0);
};

/**
 * Compares two version identifiers segment-by-segment (split on `.`) so
 * `16.1.10` sorts after `16.1.9` instead of before it, as a lexical string
 * compare would render it. Numeric segments compare numerically; a segment
 * that isn't purely numeric (e.g. `10rc1`) falls back to a numeric-aware
 * string compare (`localeCompare` with `numeric: true`) so it still degrades
 * sensibly rather than throwing or comparing as NaN.
 */
const compareVersionIds = (a, b) => {
  const as = String(a ?? '').split('.');
  const bs = String(b ?? '').split('.');
  const len = Math.max(as.length, bs.length);
  for (let i = 0; i < len; i += 1) {
    const av = as[i];
    const bv = bs[i];
    if (av === undefined) return -1;
    if (bv === undefined) return 1;
    const an = Number(av);
    const bn = Number(bv);
    const aIsNum = av !== '' && Number.isFinite(an);
    const bIsNum = bv !== '' && Number.isFinite(bn);
    if (aIsNum && bIsNum) {
      if (an !== bn) return an - bn;
    } else {
      const strCmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
      if (strCmp !== 0) return strCmp;
    }
  }
  return 0;
};

/**
 * One entry per sortable column in the versions table. `value` reads the
 * field the column displays (or its raw sortable companion, for the date);
 * `isNull` marks "no data for this column" — a version with no count/date at
 * all — which the sort below always pushes to the bottom, in either
 * direction, rather than letting it land wherever a stringified `null`/`NaN`
 * would fall.
 */
const SORT_ACCESSORS = {
  versionId: v => ({ value: v.versionId ?? '', isNull: v.versionId === undefined || v.versionId === null }),
  releaseDate: v => ({
    value: v.releaseDateRaw ?? null,
    isNull: v.releaseDateRaw === undefined || v.releaseDateRaw === null
  }),
  releaseLifeCycle: v => ({ value: v.releaseLifeCycle || '', isNull: !v.releaseLifeCycle }),
  bugCount: v => ({ value: Number(v.bugCount) || 0, isNull: itemsFor(v) === null }),
  cveCount: v => ({ value: Number(v.cveCount) || 0, isNull: itemsFor(v) === null }),
  riskScoreAverage: v => ({
    value: Number(v.riskScoreAverage) || 0,
    isNull: v.riskScoreAverage === null || v.riskScoreAverage === undefined
  }),
  // Every row resolves to a displayable state (falling back to "Not
  // reviewed"), so there is no "unknown" bucket here the way there is for
  // counts/dates.
  state: v => ({ value: v.state || NOT_REVIEWED, isNull: false })
};

const compareSortValues = (key, a, b) => {
  if (key === 'versionId') return compareVersionIds(a, b);
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }
  return a - b;
};

const VERSION_TABLE_COLUMNS = [
  { key: 'versionId', label: 'Version', align: 'left' },
  { key: 'releaseDate', label: 'Released', align: 'left' },
  { key: 'releaseLifeCycle', label: 'Lifecycle', align: 'left' },
  { key: 'bugCount', label: 'Bugs', align: 'right' },
  { key: 'cveCount', label: 'CVEs', align: 'right' },
  { key: 'riskScoreAverage', label: 'Risk', align: 'right' },
  { key: 'state', label: 'Review state', align: 'left' }
];

const ExportWizard = ({
  project,
  versions,
  currentUserEmail,
  onStarted,
  availablePriorities,
  defaultExcludedPriorities
}) => {
  const [step, setStep] = useState(0);
  const [exportMode, setExportMode] = useState('full');
  const [detailStates, setDetailStates] = useState(SELECTABLE_STATES.map(s => s.value).filter(v => v !== NOT_REVIEWED));
  const [selected, setSelected] = useState([]);
  // Report-content flag: when on, untouched bugs/CVEs (never reviewed, no
  // comment) are excluded from the report entirely. Independent of every other
  // choice; the backend StartEvent already accepts `engagedOnly: bool`.
  const [engagedOnly, setEngagedOnly] = useState(false);
  // Per-export priority-exclusion override. Only meaningful when the vendor's
  // suggestions advertise a priority scale (`availablePriorities`); until the
  // ODD side ships that metadata the control is hidden and null is sent (NO
  // override), so the server applies the vendor's own default exclusion set.
  // Seeded from the vendor default (`defaultExcludedPriorities`) so, e.g., F5
  // starts with Cosmetic excluded.
  const priorityScale = useMemo(
    () => (Array.isArray(availablePriorities) ? availablePriorities.filter(p => typeof p === 'string' && p) : []),
    [availablePriorities]
  );
  const showPriorityControl = priorityScale.length > 0;
  const seedExcluded = useMemo(
    () =>
      Array.isArray(defaultExcludedPriorities) ? defaultExcludedPriorities.filter(p => priorityScale.includes(p)) : [],
    [defaultExcludedPriorities, priorityScale]
  );
  const [excludePriorities, setExcludePriorities] = useState(seedExcluded);
  // The scale (and thus the seed) arrives asynchronously — ExportModal loads
  // suggestions after this wizard has already mounted with an empty scale. Seed
  // the pre-selection the first time a real scale shows up, but never stomp a
  // choice the user has already made (they can only touch the control once it
  // is visible, i.e. once the scale exists).
  const priorityTouched = useRef(false);
  useEffect(() => {
    if (priorityTouched.current || !showPriorityControl) return;
    setExcludePriorities(seedExcluded);
  }, [showPriorityControl, seedExcluded]);
  const [reportName, setReportName] = useState(project?.name || '');
  const [author, setAuthor] = useState(currentUserEmail || '');
  const [isCreating, setIsCreating] = useState(false);
  // Version table sort — display order only. Default is release date,
  // newest first; everything downstream that keys off `versions` (selection,
  // pricing, budget checks) reads the unsorted array on purpose so reordering
  // the table can never change what's selected or how it's priced.
  const [sortKey, setSortKey] = useState('releaseDate');
  const [sortDir, setSortDir] = useState('desc');

  // Summary has nothing to expand, so its detail step is skipped entirely.
  const steps = exportMode === 'summary' ? [0, 2, 3] : [0, 1, 2, 3];
  // Changing the mode changes which steps exist; fall back to the first rather
  // than rendering a step that is no longer in the flow.
  const safeStep = steps.includes(step) ? step : steps[0];
  const pos = steps.indexOf(safeStep);

  const share = expandedShareFor(exportMode, detailStates);

  // The versions the user has selected, looked up by id every render (cheap at
  // real version counts, per the reviewer's own call on anyBlockedByBudget).
  const selectedVersions = (versions || []).filter(v => selected.includes(v.versionId));

  // True when a selected version's size is unknown. This must block submission
  // exactly like overBudget does — an unknown size is not a small size — so it
  // is checked everywhere overBudget is checked below.
  const unknownSelected = selectedVersions.some(v => itemsFor(v) === null);

  // The exact list sent to the server, and the exact list priced locally. The
  // `?? 0` here only feeds the local estimate/estimatePages call; it is never
  // reachable in a submitted payload because unknownSelected blocks Start
  // export whenever it would apply.
  const priced = useMemo(() => selectedVersions.map(v => ({ versionId: v.versionId, items: itemsFor(v) ?? 0 })), [
    selectedVersions
  ]);

  const cost = useMemo(() => estimatePages(priced, share), [priced, share]);

  // A version whose size is unknown can never be confirmed to fit, so it fails
  // closed here rather than pricing at 0 and appearing to fit trivially.
  const wouldFit = versionId => {
    const candidate = (versions || []).find(v => v.versionId === versionId);
    if (itemsFor(candidate) === null) return false;
    const next = (versions || [])
      .filter(v => selected.includes(v.versionId) || v.versionId === versionId)
      .map(v => ({ versionId: v.versionId, items: itemsFor(v) ?? 0 }));
    return !estimatePages(next, share).overBudget;
  };

  const unselectedVersions = (versions || []).filter(v => !selected.includes(v.versionId));
  // Kept distinct from anyBlockedByBudget: the two are different problems with
  // different fixes ("remove a version" vs. "wait for its size to be known").
  const anyUnknownUnselected = unselectedVersions.some(v => itemsFor(v) === null);
  const anyBlockedByBudget = unselectedVersions.some(v => itemsFor(v) !== null && !wouldFit(v.versionId));

  // Display-only ordering for the versions table. Built from `versions`
  // (never from `selectedVersions`/`priced`), so it has no bearing on what's
  // selected, priced, or sent to the server — see the comment on `sortKey`.
  const sortedVersions = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey] || SORT_ACCESSORS.releaseDate;
    const dirMul = sortDir === 'asc' ? 1 : -1;
    return [...(versions || [])].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      // Nulls always sort last, in either direction — an unknown/unmatched
      // value is never "smallest" or "largest", it's just not comparable.
      if (av.isNull && bv.isNull) return 0;
      if (av.isNull) return 1;
      if (bv.isNull) return -1;
      return compareSortValues(sortKey, av.value, bv.value) * dirMul;
    });
  }, [versions, sortKey, sortDir]);

  const toggleSort = key => {
    if (key === sortKey) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const toggleState = value =>
    setDetailStates(prev => (prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]));

  const toggleVersion = versionId =>
    setSelected(prev => (prev.includes(versionId) ? prev.filter(v => v !== versionId) : [...prev, versionId]));

  const togglePriority = value => {
    priorityTouched.current = true;
    setExcludePriorities(prev => (prev.includes(value) ? prev.filter(p => p !== value) : [...prev, value]));
  };

  const startExport = async () => {
    try {
      setIsCreating(true);
      await BZAPIService.bsaStartExport({
        reportName: reportName.trim(),
        author: author.trim(),
        vendorId: project?.vendorId,
        bsaProjectId: project?.id,
        exportMode,
        detailStates,
        engagedOnly,
        // Only send an override when the vendor actually advertises a scale.
        // Control hidden => null (NO override), so the server/ODD apply the
        // vendor's own default exclusion set. Control shown => the user's actual
        // selected exclude-set, which may be [] to explicitly exclude nothing
        // (an empty override the server honors, not a fall-back to the default).
        excludePriorities: showPriorityControl ? excludePriorities : null,
        versions: priced
      });
      toast.success('Export started');
      if (onStarted) onStarted();
    } catch (error) {
      // The server enforces the same budget and rejects with 400 if this
      // client's estimate was optimistic. Its message names what to change.
      console.error('Failed to start export:', error);
      toast.error(error?.message || 'Failed to start export');
    } finally {
      setIsCreating(false);
    }
  };

  const detailIncomplete = exportMode !== 'summary' && detailStates.length === 0;

  // Step 3 must backstop everything the earlier steps already guard, because
  // the `versions`/other props can change out from under a user sitting on the
  // confirm step (e.g. `selected` filters down to nothing, or a selected
  // version's counts go from known to unknown). Not repeating those checks
  // here is exactly how an empty/unknown selection reaches the server.
  const blocked =
    (safeStep === 1 && detailIncomplete) ||
    (safeStep === 2 && (selected.length === 0 || cost.overBudget || unknownSelected)) ||
    (safeStep === 3 &&
      (!reportName.trim() ||
        !author.trim() ||
        selected.length === 0 ||
        cost.overBudget ||
        unknownSelected ||
        detailIncomplete));

  // Reasons Continue/Start export is disabled on the current step, rendered
  // verbatim so a blocked button is never silent (steps 1 and 2 already did
  // this; step 3 previously did not, which is the regression against the
  // shipped ExportModal's "Report name is required" / "Author is required"
  // toasts). Order is roughly most-actionable-first.
  const step3Reasons = [];
  if (!reportName.trim()) step3Reasons.push('Report name is required.');
  if (!author.trim()) step3Reasons.push('Author is required.');
  if (selected.length === 0) step3Reasons.push('Select at least one version to export.');
  if (detailIncomplete) step3Reasons.push('Pick at least one review state to expand.');
  if (unknownSelected) {
    step3Reasons.push(
      "One or more selected versions don't have item counts yet, so this report's size can't be estimated. " +
        'Go back and remove them, or wait until their counts are available.'
    );
  }
  if (cost.overBudget) {
    step3Reasons.push(
      `This selection is about ${cost.pages.toLocaleString()} pages, over the ${PAGE_BUDGET.toLocaleString()}` +
        '-page limit, so it cannot be generated. Go back and remove a version, or expand fewer review states.'
    );
  }

  const budgetPct = unknownSelected ? 100 : Math.min(100, Math.round((cost.pages / PAGE_BUDGET) * 100));

  return (
    <div className="bz-app" style={{ background: 'transparent' }}>
      {/* Step rail */}
      <div className="bz-row" style={{ gap: 'var(--sp-5)', flexWrap: 'wrap', marginBottom: 'var(--sp-6)' }}>
        {steps.map((s, i) => (
          <div key={s} className="bz-row" style={{ gap: 'var(--sp-2)' }} aria-current={i === pos ? 'step' : undefined}>
            <span
              className={`bz-pill${i <= pos ? ' bz-pill--plan' : ''}`}
              style={{ minWidth: 22, justifyContent: 'center' }}
            >
              {i + 1}
            </span>
            <span
              className={i === pos ? '' : 'bz-muted'}
              style={{ fontSize: 'var(--fs-sm)', fontWeight: i === pos ? 600 : 400 }}
            >
              {STEP_LABELS[s]}
            </span>
          </div>
        ))}
      </div>

      {/* Step 0 — report type */}
      {safeStep === 0 && (
        <div className="bz-stack" style={{ gap: 'var(--sp-3)' }}>
          {TYPES.map(t => (
            <label
              key={t.value}
              htmlFor={`mode-${t.value}`}
              className="bz-card--flat"
              style={{
                display: 'flex',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-4)',
                cursor: isCreating ? 'not-allowed' : 'pointer',
                borderColor: exportMode === t.value ? 'var(--plan)' : 'var(--line)'
              }}
            >
              <input
                type="radio"
                name="exportMode"
                id={`mode-${t.value}`}
                checked={exportMode === t.value}
                onChange={() => setExportMode(t.value)}
                disabled={isCreating}
                style={{ marginTop: 3 }}
              />
              <span>
                <span style={{ fontWeight: 600 }}>{t.label}</span>
                <span className="bz-muted" style={{ display: 'block', fontSize: 'var(--fs-sm)', marginTop: 2 }}>
                  {t.hint}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}

      {/* Step 1 — detail level */}
      {safeStep === 1 && (
        <div>
          <div className="bz-h3">Which review states get full detail?</div>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)', margin: 'var(--sp-2) 0 var(--sp-4)' }}>
            Selected states render the full block. Everything else appears as one compact line, still counted.
          </div>
          <div className="bz-row" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
            {SELECTABLE_STATES.map(s => {
              const on = detailStates.includes(s.value);
              return (
                <button
                  type="button"
                  key={s.value}
                  className={`bz-btn bz-btn--sm${on ? ' bz-btn--primary' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleState(s.value)}
                  disabled={isCreating}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          {detailIncomplete ? (
            <div
              id={BLOCK_MESSAGE_ID}
              role="alert"
              className="bz-card--flat"
              style={{
                marginTop: 'var(--sp-4)',
                padding: 'var(--sp-3) var(--sp-4)',
                borderColor: 'var(--bad)',
                background: 'var(--bad-soft)',
                color: 'var(--bad)',
                fontSize: 'var(--fs-sm)'
              }}
            >
              Pick at least one review state. An empty selection does not mean &quot;expand nothing&quot; — the report
              generator reads it as <strong>expand everything</strong>, which is the largest report it can build. To
              expand as little as possible, pick one narrow state instead.
            </div>
          ) : (
            <div className="bz-muted" style={{ marginTop: 'var(--sp-4)', fontSize: 'var(--fs-sm)' }}>
              {detailStates.includes(NOT_REVIEWED)
                ? '“Not reviewed” covers every item nobody has touched, so selecting it expands the whole report.'
                : `${detailStates.length} state${detailStates.length === 1 ? '' : 's'} expanded.`}
            </div>
          )}
        </div>
      )}

      {/* Step 2 — versions */}
      {safeStep === 2 && (
        <div>
          {(versions || []).length === 0 ? (
            <div className="bz-card--flat bz-muted" style={{ padding: 'var(--sp-6)', textAlign: 'center' }}>
              This project has no versions to export yet.
            </div>
          ) : (
            <div className="bz-card--flat bz-scroll" style={{ overflowX: 'auto' }}>
              <table className="bz-table">
                <thead>
                  <tr>
                    <th style={{ width: 34 }} aria-label="Include" />
                    {VERSION_TABLE_COLUMNS.map(col => {
                      const active = sortKey === col.key;
                      return (
                        <th
                          key={col.key}
                          style={{ textAlign: col.align === 'right' ? 'right' : undefined }}
                          aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        >
                          <button
                            type="button"
                            onClick={() => toggleSort(col.key)}
                            className="bz-row"
                            style={{
                              gap: 'var(--sp-1)',
                              alignItems: 'center',
                              justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start',
                              width: '100%',
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              font: 'inherit',
                              color: 'inherit',
                              fontWeight: active ? 600 : 'inherit',
                              cursor: 'pointer'
                            }}
                          >
                            {col.label}
                            <span aria-hidden="true" style={{ fontSize: 'var(--fs-xs)', opacity: active ? 1 : 0.35 }}>
                              {active && sortDir === 'asc' ? '▲' : '▼'}
                            </span>
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortedVersions.map(v => {
                    const on = selected.includes(v.versionId);
                    const unknown = itemsFor(v) === null;
                    const disabled = isCreating || (!on && !wouldFit(v.versionId));
                    const tooltip =
                      disabled && !isCreating
                        ? unknown
                          ? `${v.versionId}'s item count isn't known yet, so it can't be safely included`
                          : `Adding ${v.versionId} would push this report past ${PAGE_BUDGET} pages`
                        : undefined;
                    return (
                      <tr
                        key={v.versionId}
                        className={disabled ? 'bz-muted' : ''}
                        title={tooltip}
                        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                        onClick={e => {
                          if (disabled) return;
                          if (e.target.tagName === 'INPUT') return;
                          toggleVersion(v.versionId);
                        }}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={disabled}
                            aria-label={`Include ${v.versionId}`}
                            onChange={() => toggleVersion(v.versionId)}
                          />
                        </td>
                        <td style={{ fontWeight: 600 }}>
                          {v.versionId}
                          {unknown && (
                            <span className="bz-pill bz-pill--warn" style={{ marginLeft: 'var(--sp-2)' }}>
                              Size unknown
                            </span>
                          )}
                        </td>
                        <td>{v.releaseDate || '—'}</td>
                        <td>{v.releaseLifeCycle || '—'}</td>
                        <td className="bz-tnum" style={{ textAlign: 'right' }}>
                          {unknown ? '—' : (Number(v.bugCount) || 0).toLocaleString()}
                        </td>
                        <td className="bz-tnum" style={{ textAlign: 'right' }}>
                          {unknown ? '—' : (Number(v.cveCount) || 0).toLocaleString()}
                        </td>
                        <td className="bz-tnum" style={{ textAlign: 'right' }}>
                          {v.riskScoreAverage ?? '—'}
                        </td>
                        <td>{v.state || NOT_REVIEWED}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="bz-card--flat" style={{ padding: 'var(--sp-4)', marginTop: 'var(--sp-4)' }}>
            <div className="bz-row" style={{ justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
              <span>
                {unknownSelected ? (
                  <strong>Unknown</strong>
                ) : (
                  <strong className="bz-tnum">~{cost.pages.toLocaleString()}</strong>
                )}{' '}
                pages estimated
                <span className="bz-muted"> of {PAGE_BUDGET.toLocaleString()}</span>
              </span>
              <span className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
                {selected.length} version{selected.length === 1 ? '' : 's'} · {cost.unique.toLocaleString()} unique
                items
              </span>
            </div>
            <div className="bz-bar" style={{ margin: 'var(--sp-3) 0' }}>
              <span
                style={{
                  width: `${budgetPct}%`,
                  background: cost.overBudget || unknownSelected ? 'var(--bad)' : 'var(--ok)'
                }}
              />
            </div>
            {cost.overBudget && (
              <div id={BLOCK_MESSAGE_ID} role="alert" style={{ color: 'var(--bad)', fontSize: 'var(--fs-sm)' }}>
                This selection is about {cost.pages.toLocaleString()} pages, over the {PAGE_BUDGET.toLocaleString()}
                -page limit, so it cannot be generated. Remove a version, or go back and expand fewer review states.
              </div>
            )}
            {!cost.overBudget && unknownSelected && (
              <div id={BLOCK_MESSAGE_ID} role="alert" style={{ color: 'var(--bad)', fontSize: 'var(--fs-sm)' }}>
                One or more selected versions don&apos;t have item counts yet, so this report&apos;s size can&apos;t be
                estimated. Remove them from the selection, or wait until their counts are available.
              </div>
            )}
            {!cost.overBudget && !unknownSelected && anyUnknownUnselected && (
              <div style={{ color: 'var(--warn)', fontSize: 'var(--fs-sm)' }}>
                Some versions don&apos;t have item counts yet, so they can&apos;t be safely included. They&apos;ll
                become selectable once their size is known.
              </div>
            )}
            {!cost.overBudget && !unknownSelected && anyBlockedByBudget && (
              <div style={{ color: 'var(--warn)', fontSize: 'var(--fs-sm)' }}>
                Some versions are unavailable — they would push this report past what can be generated. Expand fewer
                review states, or remove a version.
              </div>
            )}
            {!cost.overBudget &&
              !unknownSelected &&
              !anyBlockedByBudget &&
              !anyUnknownUnselected &&
              selected.length === 0 && (
                <div id={BLOCK_MESSAGE_ID} role="alert" className="bz-muted" style={{ fontSize: 'var(--fs-sm)' }}>
                  Select at least one version to export.
                </div>
              )}
          </div>
        </div>
      )}

      {/* Step 3 — confirm */}
      {safeStep === 3 && (
        <div>
          <div style={{ marginBottom: 'var(--sp-4)' }}>
            <label htmlFor="exportReportName" className="bz-kicker" style={{ display: 'block', marginBottom: 4 }}>
              Report name *
            </label>
            <span className="bz-input" style={{ display: 'flex', width: '100%' }}>
              <input
                id="exportReportName"
                type="text"
                value={reportName}
                maxLength={128}
                placeholder="e.g., Q4 2024 Cisco Analysis"
                onChange={e => setReportName(e.target.value)}
                disabled={isCreating}
                required
                aria-required="true"
              />
            </span>
          </div>

          <div style={{ marginBottom: 'var(--sp-4)' }}>
            <label htmlFor="exportAuthor" className="bz-kicker" style={{ display: 'block', marginBottom: 4 }}>
              Author *
            </label>
            <span className="bz-input" style={{ display: 'flex', width: '100%' }}>
              <input
                id="exportAuthor"
                type="text"
                value={author}
                maxLength={128}
                placeholder="Your email address"
                onChange={e => setAuthor(e.target.value)}
                disabled={isCreating}
                required
                aria-required="true"
              />
            </span>
          </div>

          <div className="bz-card--flat" style={{ padding: 'var(--sp-4)' }}>
            <strong>{TYPES.find(t => t.value === exportMode)?.label}</strong> report · {selected.length} version
            {selected.length === 1 ? '' : 's'} ·{' '}
            {unknownSelected ? (
              <>size can&apos;t be estimated (one or more versions have an unknown item count)</>
            ) : (
              <>
                approximately <strong className="bz-tnum">{cost.pages.toLocaleString()} pages</strong>
              </>
            )}
            <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 4 }}>
              Page count is an estimate from measured rendering costs, not a guarantee.
            </div>
          </div>

          {/* Report-content option — independent of every other choice. */}
          <label
            htmlFor="exportEngagedOnly"
            className="bz-card--flat"
            style={{
              display: 'flex',
              gap: 'var(--sp-3)',
              padding: 'var(--sp-4)',
              marginTop: 'var(--sp-4)',
              cursor: isCreating ? 'not-allowed' : 'pointer'
            }}
          >
            <input
              id="exportEngagedOnly"
              type="checkbox"
              checked={engagedOnly}
              onChange={e => setEngagedOnly(e.target.checked)}
              disabled={isCreating}
              style={{ marginTop: 3 }}
            />
            <span>
              <span style={{ fontWeight: 600 }}>Only include reviewed or commented bugs</span>
              <span className="bz-muted" style={{ display: 'block', fontSize: 'var(--fs-sm)', marginTop: 2 }}>
                Untouched bugs and CVEs — never reviewed and with no comment — are left out of the report entirely.
              </span>
            </span>
          </label>

          {/* Priority-exclusion control — only shown when the vendor's
              suggestions advertise a priority scale. Absent that metadata (every
              tenant until the ODD side ships it) the whole block is hidden and []
              is sent, so the server keeps applying the vendor default. */}
          {showPriorityControl && (
            <div className="bz-card--flat" style={{ padding: 'var(--sp-4)', marginTop: 'var(--sp-4)' }}>
              <div style={{ fontWeight: 600 }}>Exclude priorities</div>
              <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)', margin: 'var(--sp-1) 0 var(--sp-3)' }}>
                Bugs and CVEs at the selected priorities are left out of the report. Pre-set to the vendor&apos;s
                recommendation; adjust for this export only.
              </div>
              <div className="bz-row" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                {priorityScale.map(p => {
                  const on = excludePriorities.includes(p);
                  return (
                    <button
                      type="button"
                      key={p}
                      className={`bz-btn bz-btn--sm${on ? ' bz-btn--primary' : ''}`}
                      aria-pressed={on}
                      onClick={() => togglePriority(p)}
                      disabled={isCreating}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
              <div className="bz-muted" style={{ marginTop: 'var(--sp-3)', fontSize: 'var(--fs-sm)' }}>
                {excludePriorities.length === 0
                  ? 'Nothing excluded — every priority is included.'
                  : `Excluding ${excludePriorities.length} of ${priorityScale.length} priorities.`}
              </div>
            </div>
          )}

          {step3Reasons.length > 0 && (
            <div
              id={BLOCK_MESSAGE_ID}
              role="alert"
              className="bz-card--flat"
              style={{
                marginTop: 'var(--sp-4)',
                padding: 'var(--sp-3) var(--sp-4)',
                borderColor: 'var(--bad)',
                background: 'var(--bad-soft)',
                color: 'var(--bad)',
                fontSize: 'var(--fs-sm)'
              }}
            >
              {step3Reasons.length === 1 ? (
                step3Reasons[0]
              ) : (
                <ul style={{ margin: 0, paddingLeft: 'var(--sp-4)' }}>
                  {step3Reasons.map(reason => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Nav */}
      <div className="bz-row" style={{ justifyContent: 'space-between', marginTop: 'var(--sp-6)' }}>
        <button
          type="button"
          className="bz-btn"
          disabled={pos === 0 || isCreating}
          onClick={() => setStep(steps[pos - 1])}
        >
          Back
        </button>
        <button
          type="button"
          className="bz-btn bz-btn--primary"
          disabled={blocked || isCreating}
          aria-describedby={blocked ? BLOCK_MESSAGE_ID : undefined}
          onClick={() => (pos === steps.length - 1 ? startExport() : setStep(steps[pos + 1]))}
        >
          {isCreating ? <Spinner size="sm" /> : pos === steps.length - 1 ? 'Start export' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

ExportWizard.propTypes = {
  project: PropTypes.object.isRequired,
  versions: PropTypes.array,
  currentUserEmail: PropTypes.string,
  onStarted: PropTypes.func,
  // The vendor's ordered priority scale (most-severe first) and its default
  // exclusion set, read off the software suggestions by ExportModal. Both
  // default to [] — the pre-metadata reality — which hides the control.
  availablePriorities: PropTypes.arrayOf(PropTypes.string),
  defaultExcludedPriorities: PropTypes.arrayOf(PropTypes.string)
};

export default ExportWizard;
